import React, { useState, useContext } from 'react';
import {
  Tabs,
  Button,
  Textarea,
  Box,
  Icon,
  SpaceBetween,
  Toggle,
  Spinner,
} from '@amzn/awsui-components-core';
import {
  InlineComment,
  UserCommentType,
  UserCommentState,
  ContentType,
} from '@amzn/awscat-pattern-library-service-model';

import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import InlineCommentCtx from './InlineCommentCtx';
import { updateStatesInLocalStorage } from '../utils/InlineCommentUtils';
import { ContentViewerContext } from '../workflow-viewer/ContentViewerContext';

interface Props {
  threadComment: InlineComment;
  contentId: string;
  workflowId: number;
}

const InlineCommentReply = ({ threadComment, contentId, workflowId }: Props) => {
  const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();
  const { allInlineComments, fetchInlineComments } = useContext(InlineCommentCtx);
  const contentViewerContext = useContext(ContentViewerContext);

  const [isReplyMode, setIsReplyMode] = useState<boolean>(false);
  const [isCreatingReply, setIsCreatingReply] = useState<boolean>(false);
  const [replyTextData, setReplyTextData] = useState<string>('');
  const [isCommentResolved, setIsCommentResolved] = useState<boolean>(
    threadComment.state === UserCommentState.RESOLVED,
  );
  const [isResolvingComment, setIsResolvingComment] = useState<boolean>(false);

  const handleClickReply = () => {
    setIsReplyMode(true);
  };

  // @ts-expect-error TS7031 - strict migration
  const handleReplyChange = ({ detail }) => {
    setReplyTextData(detail.value);
  };

  const handleCancelReply = () => {
    setIsReplyMode(false);
    setReplyTextData('');
  };

  const handleCreateReply = async () => {
    setIsCreatingReply(true);
    const { path, textData, startOffset, endOffset, commentId } = threadComment;
    await mplServiceClient.createInlineComment(
      path,
      contentId,
      workflowId,
      UserCommentType.Reply,
      replyTextData,
      textData,
      commentId,
      startOffset,
      endOffset,
    );
    setIsCreatingReply(false);
    setReplyTextData('');
    setIsReplyMode(false);
    await fetchInlineComments();
  };

  // @ts-expect-error TS7031 - strict migration
  const handleToggleResolve = async ({ detail }) => {
    setIsResolvingComment(true);
    setIsCommentResolved(detail.checked);
    const commentsToResolve = allInlineComments.filter(
      (comment) =>
        comment.threadId === threadComment.commentId ||
        comment.commentId === threadComment.commentId,
    );
    if (!commentsToResolve.length) {
      return;
    }
    const updateCommentAttributes = commentsToResolve.map((comment) => ({
      commentId: comment.commentId,
      state: detail.checked ? UserCommentState.RESOLVED : UserCommentState.PENDING,
    }));
    await mplServiceClient.updateInlineComments(contentId, workflowId, updateCommentAttributes);
    // @ts-expect-error TS2532 - strict migration
    let sectionId = commentsToResolve[0].path[0];
    const contentType = contentViewerContext?.content?.contentMetadata?.contentType;
    // @ts-expect-error TS2345 - strict migration
    if ([ContentType.Guide, ContentType.Strategy].includes(contentType)) {
      // @ts-expect-error TS2532 - strict migration
      sectionId = commentsToResolve[0].path[0] + commentsToResolve[0].path?.[1];
    }
    setIsResolvingComment(false);
    await contentViewerContext?.reFetchInlineComments?.();
    updateStatesInLocalStorage(
      contentId,
      commentsToResolve.map((comment) => ({
        ...comment,
        state: detail.checked ? UserCommentState.RESOLVED : UserCommentState.PENDING,
      })),
      // @ts-expect-error TS2345 - strict migration
      sectionId,
    );
  };

  const renderReplyInput = () => {
    return (
      <SpaceBetween direction="vertical" size="xs">
        <Textarea
          onChange={handleReplyChange}
          value={replyTextData}
          placeholder="Add your response"
          ariaRequired
        />
        <SpaceBetween direction="horizontal" size="xs">
          <Button onClick={handleCancelReply}>Cancel</Button>
          <Button variant="primary" onClick={handleCreateReply} loading={isCreatingReply}>
            Save
          </Button>
        </SpaceBetween>
      </SpaceBetween>
    );
  };

  const renderResolveCommentToggle = () => {
    if (isResolvingComment) {
      return <Spinner />;
    }
    return (
      <div className="inline-comment-list-resolve-toggle">
        <Toggle checked={isCommentResolved} onChange={handleToggleResolve}>
          Mark as resolved
        </Toggle>
      </div>
    );
  };

  const replyIcon = (
    <Button variant="link" onClick={handleClickReply}>
      <Icon name="undo" variant="link" />
      <Box color="text-status-info" display="inline">
        Reply
      </Box>
    </Button>
  );

  return (
    <div className={`inline-comment-list-reply-${isReplyMode ? 'input' : 'icon'}`}>
      {isReplyMode ? renderReplyInput() : replyIcon}
      {renderResolveCommentToggle()}
    </div>
  );
};

export default InlineCommentReply;
